import { Badge, Group, Text, Avatar, Stack, Box, Anchor, Divider } from '@mantine/core';
import { IconVideo, IconMapPin, IconExternalLink } from '@tabler/icons-react';
import { CareSessionModelView } from '@actschurch/ccms-sdk';

function getContrastText(hex?: string | null): string {
  if (!hex) return '#fff';
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#fff';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1A1B1E' : '#fff';
}

export function ColorBadge({ label, color }: { label: string; color?: string | null }) {
  return (
    <Badge
      variant="light"
      style={{
        backgroundColor: color ?? 'var(--mantine-color-gray-5)',
        color: getContrastText(color),
      }}
    >
      {label}
    </Badge>
  );
}

interface SessionDetailsProps {
  session: CareSessionModelView;
}

export default function SessionDetails({ session }: SessionDetailsProps) {
  return (
    <>
      <Divider
        label={<Text size="sm" fw={600}>Session details</Text>}
        labelPosition="center"
        mb="md"
      />
      <Stack gap="md">
        <Group gap="xs">
          <ColorBadge label={session.typeName ?? 'N/A'} color={session.typeColor} />
          <ColorBadge label={session.statusName ?? 'N/A'} color={session.statusColor} />
        </Group>

        <Group gap={8}>
          <Avatar src={session.leaderAvatar} size="sm" radius="xl">
            {!session.leaderAvatar && (session.leaderName?.charAt(0).toUpperCase() ?? '?')}
          </Avatar>
          <Text size="sm">{session.leaderName ?? 'Unassigned'}</Text>
        </Group>

        <Group gap="xl">
          <Box>
            <Text size="xs" c="dimmed">Date</Text>
            <Text size="sm">{session.sessionDateDisplay ?? '—'}</Text>
          </Box>
          <Box>
            <Text size="xs" c="dimmed">Mode</Text>
            <Group gap={4}>
              {session.link ? <IconVideo size={14} /> : <IconMapPin size={14} />}
              <Text size="sm">{session.modeDisplay ?? '—'}</Text>
            </Group>
          </Box>
        </Group>

        {session.link && (
          <Anchor href={session.link} target="_blank" size="sm">
            <Group gap={4}>
              <IconExternalLink size={14} />
              Join virtual link
            </Group>
          </Anchor>
        )}

        {session.locationName && (
          <Box>
            <Text size="xs" c="dimmed">Location</Text>
            <Text size="sm">{session.locationName}</Text>
          </Box>
        )}

        {session.description && (
          <Box>
            <Text size="xs" c="dimmed">Description</Text>
            <Text size="sm">{session.description}</Text>
          </Box>
        )}

        {session.details && (
          <Box>
            <Text size="xs" c="dimmed">Details</Text>
            <Text size="sm">{session.details}</Text>
          </Box>
        )}

        {session.cases && session.cases.length > 0 && (
          <Box>
            <Text size="xs" c="dimmed" mb={4}>Linked cases</Text>
            <Group gap={6}>
              {session.cases.map((c) => (
                <Badge key={c.caseID} variant="default" size="sm">
                  {c.caseName ?? `Case ${c.caseID}`}
                </Badge>
              ))}
            </Group>
          </Box>
        )}
      </Stack>
    </>
  );
}